/**
 * RefiMonitorSettingsManager — admin panel for the Mortgage Monitor
 * (config/crmSettings.refiMonitor). Embedded in the Admin drawer on the
 * Mortgage Monitor page.
 *
 * Covers qualification thresholds, estimated LO comp (bps of refinanced
 * balance), and per-loan-type Today's Rates: FHA/VA/USDA/Non-QM derive from
 * the conventional 30yr PMMS rate via a spread (bps), or a flat manual
 * override when set. Saved via the updateCrmSettings callable; defaults
 * mirror DEFAULT_MONITOR_SETTINGS in functions/CRM/monitor/loanMath.js.
 */

import React, { useState, useEffect } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  SimpleGrid,
  Switch,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { FiActivity, FiDollarSign, FiPercent, FiSave } from 'react-icons/fi';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../../utils/init-firebase';
import { useCrmSettings } from '../../../contexts/CrmSettingsContext';

const RATE_TYPES = ['FHA', 'VA', 'USDA', 'NonQM'];
const RATE_TYPE_LABELS = { FHA: 'FHA 30yr', VA: 'VA 30yr', USDA: 'USDA 30yr', NonQM: 'Non-QM 30yr' };

const DEFAULTS = {
  minRateDeltaBps: 50,
  minMonthlySavings: 50,
  minMonthsSeasoned: 6,
  annualAppreciationPct: 3,
  equityFlagPct: 20,
  loCompBps: 150,
  rateSpreadsBps: { FHA: -25, VA: -50, USDA: -25, NonQM: 150 },
  rateOverrides: { FHA: null, VA: null, USDA: null, NonQM: null },
  loanSifterEnabled: true,
  tagName: 'Refi Opportunity',
};

const fmtPreviewRate = (r) => `${(Math.round(r * 1000) / 1000).toFixed(3).replace(/0+$/, '').replace(/\.$/, '')}%`;

/**
 * @param {number|null} conventionalRate latest conventional 30yr rate for the
 *   live per-type previews (optional).
 * @param {Function} onSaved called after a successful save so the host page
 *   can re-derive rates and reload.
 */
export default function RefiMonitorSettingsManager({ conventionalRate = null, onSaved }) {
  const toast = useToast();
  const crmSettings = useCrmSettings();
  const refiMonitor = crmSettings?.refiMonitor;
  const [form, setForm] = useState({ ...DEFAULTS });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const helperColor = useColorModeValue('gray.600', 'gray.400');
  const rowBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    if (refiMonitor && typeof refiMonitor === 'object') {
      setForm({
        ...DEFAULTS,
        ...refiMonitor,
        rateSpreadsBps: { ...DEFAULTS.rateSpreadsBps, ...(refiMonitor.rateSpreadsBps || {}) },
        rateOverrides: { ...DEFAULTS.rateOverrides, ...(refiMonitor.rateOverrides || {}) },
      });
      setDirty(false);
    }
  }, [refiMonitor]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const numberField = (key, value) => {
    const num = Number(value);
    setField(key, Number.isFinite(num) ? num : DEFAULTS[key]);
  };

  const setSpread = (type, value) => {
    setForm((prev) => ({ ...prev, rateSpreadsBps: { ...prev.rateSpreadsBps, [type]: value } }));
    setDirty(true);
  };

  const setOverride = (type, value) => {
    setForm((prev) => ({ ...prev, rateOverrides: { ...prev.rateOverrides, [type]: value } }));
    setDirty(true);
  };

  /** Resulting Today's Rate for one type under the current form values. */
  const previewFor = (type) => {
    const override = form.rateOverrides?.[type];
    if (override !== null && override !== undefined && override !== '' && Number.isFinite(Number(override))) {
      return { rate: Number(override), from: 'override' };
    }
    if (!Number.isFinite(Number(conventionalRate))) return null;
    const spread = Number(form.rateSpreadsBps?.[type]) || 0;
    return { rate: Number(conventionalRate) + spread / 100, from: 'spread' };
  };

  const handleSave = async () => {
    if (!form.tagName || !String(form.tagName).trim()) {
      toast({ title: 'Enroll tag name is required', status: 'warning', duration: 4000 });
      return;
    }
    setSaving(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const updateCrmSettings = httpsCallable(functions, 'updateCrmSettings');

      const rateSpreadsBps = {};
      const rateOverrides = {};
      for (const type of RATE_TYPES) {
        const spread = Number(form.rateSpreadsBps?.[type]);
        rateSpreadsBps[type] = Number.isFinite(spread) ? spread : DEFAULTS.rateSpreadsBps[type];
        const override = form.rateOverrides?.[type];
        rateOverrides[type] =
          override === null || override === undefined || override === '' || !Number.isFinite(Number(override))
            ? null
            : Number(override);
      }

      await updateCrmSettings({
        updates: {
          refiMonitor: {
            minRateDeltaBps: Number(form.minRateDeltaBps),
            minMonthlySavings: Number(form.minMonthlySavings),
            minMonthsSeasoned: Number(form.minMonthsSeasoned),
            annualAppreciationPct: Number(form.annualAppreciationPct),
            equityFlagPct: Number(form.equityFlagPct),
            loCompBps: Number(form.loCompBps),
            rateSpreadsBps,
            rateOverrides,
            loanSifterEnabled: form.loanSifterEnabled !== false,
            tagName: String(form.tagName).trim(),
          },
        },
      });
      setDirty(false);
      toast({
        title: 'Mortgage Monitor settings saved',
        description: 'Rates are being re-derived and the next scan will use the new thresholds.',
        status: 'success',
        duration: 5000,
      });
      if (onSaved) onSaved();
    } catch (error) {
      console.error('Failed to save refi monitor settings:', error);
      toast({ title: 'Save failed', description: error.message, status: 'error', duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <HStack spacing={2} mb={1}>
        <Icon as={FiActivity} color="blue.400" />
        <Heading size="sm">Thresholds &amp; Rates</Heading>
      </HStack>
      <Text fontSize="sm" color={helperColor} mb={4}>
        Loans qualify when they beat <strong>both</strong> the rate-delta and monthly-savings minimums
        after the seasoning window. Saving re-derives the per-type Today&apos;s Rates immediately.
      </Text>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
        <FormControl>
          <FormLabel fontSize="sm">Min rate delta (bps)</FormLabel>
          <NumberInput size="sm" value={form.minRateDeltaBps} min={10} max={300} step={5}
            onChange={(_, v) => numberField('minRateDeltaBps', v)}>
            <NumberInputField />
            <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
          </NumberInput>
          <FormHelperText fontSize="xs">Borrower note rate above market by at least this much</FormHelperText>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm">Min monthly savings ($)</FormLabel>
          <NumberInput size="sm" value={form.minMonthlySavings} min={0} max={1000} step={10}
            onChange={(_, v) => numberField('minMonthlySavings', v)}>
            <NumberInputField />
            <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
          </NumberInput>
          <FormHelperText fontSize="xs">Estimated P&amp;I savings on the remaining term</FormHelperText>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm">Min seasoning (months)</FormLabel>
          <NumberInput size="sm" value={form.minMonthsSeasoned} min={0} max={36} step={1}
            onChange={(_, v) => numberField('minMonthsSeasoned', v)}>
            <NumberInputField />
            <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
          </NumberInput>
          <FormHelperText fontSize="xs">Skip recent fundings (EPO windows)</FormHelperText>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm">Annual appreciation (%)</FormLabel>
          <NumberInput size="sm" value={form.annualAppreciationPct} min={0} max={15} step={0.5}
            onChange={(_, v) => numberField('annualAppreciationPct', v)}>
            <NumberInputField />
            <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
          </NumberInput>
          <FormHelperText fontSize="xs">Flat growth assumption for property values (no AVM)</FormHelperText>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm">Equity flag (%)</FormLabel>
          <NumberInput size="sm" value={form.equityFlagPct} min={5} max={80} step={5}
            onChange={(_, v) => numberField('equityFlagPct', v)}>
            <NumberInputField />
            <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
          </NumberInput>
          <FormHelperText fontSize="xs">Mark equity-rich loans at or above this estimated equity</FormHelperText>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm">Enroll tag name</FormLabel>
          <Input size="sm" value={form.tagName} onChange={(e) => setField('tagName', e.target.value)} />
          <FormHelperText fontSize="xs">
            Applied to the borrower contact on Enroll — fires TAG_ADDED campaigns on this tag
          </FormHelperText>
        </FormControl>
      </SimpleGrid>

      <Divider my={5} />

      <HStack spacing={2} mb={1}>
        <Icon as={FiDollarSign} color="green.400" />
        <Heading size="sm">Est. LO Compensation</Heading>
      </HStack>
      <FormControl maxW="280px" mt={2}>
        <FormLabel fontSize="sm">Comp (bps of refinanced balance)</FormLabel>
        <NumberInput size="sm" value={form.loCompBps} min={0} max={500} step={5}
          onChange={(_, v) => numberField('loCompBps', v)}>
          <NumberInputField />
          <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
        </NumberInput>
        <FormHelperText fontSize="xs">
          150 bps = 1.5% — the &quot;Est. LO Comp&quot; card is actionable balance × this
        </FormHelperText>
      </FormControl>

      <Divider my={5} />

      <HStack spacing={2} mb={1}>
        <Icon as={FiPercent} color="purple.400" />
        <Heading size="sm">Per-Loan-Type Today&apos;s Rates</Heading>
      </HStack>
      <Text fontSize="sm" color={helperColor} mb={3}>
        With the LoanSifter sheet on, Conventional/FHA/VA/USDA come from the team&apos;s live best-execution
        wholesale pricing whenever the sheet is fresh (under ~4 days old), and Non-QM derives from that
        conventional rate via its spread. When the sheet is off or stale, Conventional falls back to FRED
        (Freddie Mac PMMS) and each type derives via the spread. A flat % override always wins for its
        type, in both modes.
        {Number.isFinite(Number(conventionalRate)) && (
          <> Conventional today: <strong>{fmtPreviewRate(Number(conventionalRate))}</strong>.</>
        )}
      </Text>

      <FormControl display="flex" alignItems="center" mb={4}>
        <Switch
          id="loansifter-enabled"
          isChecked={form.loanSifterEnabled !== false}
          onChange={(e) => setField('loanSifterEnabled', e.target.checked)}
          mr={3}
        />
        <Box>
          <FormLabel htmlFor="loansifter-enabled" mb={0} fontSize="sm">Use LoanSifter sheet rates</FormLabel>
          <Text fontSize="xs" color={helperColor}>
            Off = FRED-derived rates only. Takes effect at the next rate fetch (daily 6:00 AM ET or
            &quot;Fetch market rates now&quot;).
          </Text>
        </Box>
      </FormControl>

      <VStack align="stretch" spacing={2}>
        {RATE_TYPES.map((type) => {
          const preview = previewFor(type);
          return (
            <HStack key={type} bg={rowBg} borderRadius="md" px={3} py={2} spacing={4} wrap="wrap">
              <Text fontSize="sm" fontWeight="600" w="92px">{RATE_TYPE_LABELS[type]}</Text>
              <FormControl w="150px">
                <HStack spacing={2}>
                  <NumberInput size="xs" value={form.rateSpreadsBps?.[type] ?? 0} min={-300} max={500} step={5}
                    onChange={(_, v) => setSpread(type, Number.isFinite(v) ? v : 0)}>
                    <NumberInputField />
                    <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="xs" color={helperColor} whiteSpace="nowrap">bps spread</Text>
                </HStack>
              </FormControl>
              <FormControl w="170px">
                <HStack spacing={2}>
                  <NumberInput
                    size="xs"
                    value={form.rateOverrides?.[type] ?? ''}
                    min={0}
                    max={15}
                    step={0.125}
                    onChange={(str) => setOverride(type, str === '' ? null : str)}
                  >
                    <NumberInputField placeholder="—" />
                  </NumberInput>
                  <Text fontSize="xs" color={helperColor} whiteSpace="nowrap">% override</Text>
                </HStack>
              </FormControl>
              {preview ? (
                <Badge colorScheme={preview.from === 'override' ? 'orange' : 'purple'} fontSize="0.7rem">
                  {fmtPreviewRate(preview.rate)} {preview.from === 'override' ? '(override)' : '(derived)'}
                </Badge>
              ) : (
                <Badge colorScheme="gray" fontSize="0.7rem">no preview</Badge>
              )}
            </HStack>
          );
        })}
      </VStack>

      <VStack align="flex-start" mt={5}>
        <Button
          size="sm"
          colorScheme="blue"
          leftIcon={<FiSave />}
          onClick={handleSave}
          isLoading={saving}
          isDisabled={!dirty}
        >
          Save Mortgage Monitor settings
        </Button>
      </VStack>
    </Box>
  );
}
